import React, { useState, useEffect } from 'react';
import './SeatingPlan.css';
import { Guest, TABLES } from './seatingTypes';
import PrintView from './PrintView';

const SeatingPlan: React.FC = () => {
  const [assignments, setAssignments] = useState<Map<string, Guest>>(new Map());
  const [unassignedGuests, setUnassignedGuests] = useState<Guest[]>([]);
  const [draggedGuest, setDraggedGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showPrintView, setShowPrintView] = useState(false);
  const [tableOrder, setTableOrder] = useState<string[]>(['table1', 'table2', 'table3', 'table4']);
  const [draggedTable, setDraggedTable] = useState<string | null>(null);

  useEffect(() => {
    fetchGuests();
    fetchTableOrder();
  }, []);

  const fetchTableOrder = async () => {
    try {
      const response = await fetch('/api/admin/table-order');
      const data = await response.json();
      if (data.order) {
        setTableOrder(data.order);
      }
    } catch (error) {
      console.error('Error fetching table order:', error);
    }
  };

  const saveTableOrder = async (newOrder: string[]) => {
    try {
      await fetch('/api/admin/table-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: newOrder }),
      });
    } catch (error) {
      console.error('Error saving table order:', error);
    }
  };

  const fetchGuests = async () => {
    try {
      // Fetch guests invited to wedding dinner event
      const response = await fetch('/api/admin/guests-for-seating');
      const data = await response.json();

      // Build assignment map
      const assignmentMap = new Map<string, Guest>();
      const unassigned: Guest[] = [];

      data.forEach((guest: Guest) => {
        if (guest.tableAssignment) {
          const key = `${guest.tableAssignment.tableName}-${guest.tableAssignment.seatIndex}`;
          assignmentMap.set(key, guest);
        } else {
          unassigned.push(guest);
        }
      });

      setAssignments(assignmentMap);
      setUnassignedGuests(unassigned);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching guests:', error);
      setLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, guest: Guest) => {
    e.stopPropagation();
    setDraggedGuest(guest);
    setDraggedTable(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleTableDragStart = (tableKey: string) => {
    setDraggedTable(tableKey);
  };

  const handleTableDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleTableDrop = async (targetTableKey: string) => {
    if (!draggedTable || draggedTable === targetTableKey) {
      setDraggedTable(null);
      return;
    }

    const newOrder = [...tableOrder];
    const draggedIndex = newOrder.indexOf(draggedTable);
    const targetIndex = newOrder.indexOf(targetTableKey);

    // Remove dragged table and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedTable);

    setTableOrder(newOrder);
    setDraggedTable(null);

    // Save to database
    await saveTableOrder(newOrder);
  };

  const handleDrop = async (tableName: string, seatIndex: number) => {
    if (!draggedGuest) return;

    try {
      // Save assignment to backend
      const response = await fetch('/api/admin/table-assignment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guestId: draggedGuest.id,
          tableName,
          seatIndex,
        }),
      });

      if (response.ok) {
        // Update local state
        const newAssignments = new Map(assignments);
        const key = `${tableName}-${seatIndex}`;

        // Remove from previous position
        if (draggedGuest.tableAssignment) {
          const oldKey = `${draggedGuest.tableAssignment.tableName}-${draggedGuest.tableAssignment.seatIndex}`;
          newAssignments.delete(oldKey);
        } else {
          // Remove from unassigned
          setUnassignedGuests(unassignedGuests.filter(g => g.id !== draggedGuest.id));
        }

        // Add to new position
        const updatedGuest = {
          ...draggedGuest,
          tableAssignment: { tableName, seatIndex },
        };
        newAssignments.set(key, updatedGuest);

        // Apply all changes at once
        setAssignments(newAssignments);
      }
    } catch (error) {
      console.error('Error saving assignment:', error);
    }

    setDraggedGuest(null);
  };

  const handleRemoveAssignment = async (guest: Guest) => {
    if (!guest.tableAssignment) return;

    try {
      const response = await fetch(`/api/admin/table-assignment/${guest.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove from assignments
        const key = `${guest.tableAssignment.tableName}-${guest.tableAssignment.seatIndex}`;
        const newAssignments = new Map(assignments);
        newAssignments.delete(key);
        setAssignments(newAssignments);

        // Add to unassigned
        const updatedGuest = { ...guest, tableAssignment: null };
        setUnassignedGuests([...unassignedGuests, updatedGuest]);
      }
    } catch (error) {
      console.error('Error removing assignment:', error);
    }
  };

  const getRsvpStatus = (guest: Guest): 'confirmed' | 'declined' | 'pending' => {
    if (!guest.rsvps || guest.rsvps.length === 0) {
      return 'pending';
    }
    return guest.rsvps[0].attending ? 'confirmed' : 'declined';
  };

  const renderSeat = (tableName: string, seatIndex: number, seatType?: 'left' | 'right' | 'end' | 'top' | 'bottom') => {
    const key = `${tableName}-${seatIndex}`;
    const guest = assignments.get(key);
    const seatClass = seatType ? `seat-${seatType}` : '';
    const rsvpStatus = guest ? getRsvpStatus(guest) : null;

    return (
      <div
        key={seatIndex}
        className={`seat ${guest ? 'occupied' : 'empty'} ${seatClass}`}
        onDragOver={handleDragOver}
        onDrop={(e) => { e.stopPropagation(); handleDrop(tableName, seatIndex); }}
      >
        {guest ? (
          <div
            className={`guest-card rsvp-${rsvpStatus}`}
            draggable
            onDragStart={(e) => handleDragStart(e, guest)}
          >
            <span className="guest-name">
              {guest.firstName} {guest.lastName || ''}
            </span>
            <button
              className="remove-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveAssignment(guest);
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <div className="seat-number-wrapper">
            <span className="seat-number">{seatIndex + 1}</span>
          </div>
        )}
      </div>
    );
  };

  const renderHonorTable = () => {
    const table = TABLES.honor;
    const seatsPerSide = table.seats / 2;

    return (
      <div className="table honor-table">
        <h3>{table.name}</h3>
        <div className="table-layout horizontal">
          <div className="table-side top">
            {Array.from({ length: seatsPerSide }, (_, i) => renderSeat('honor', i, 'top'))}
          </div>
          <div className="table-rectangle"></div>
          <div className="table-side bottom">
            {Array.from({ length: seatsPerSide }, (_, i) => renderSeat('honor', i + seatsPerSide, 'bottom'))}
          </div>
        </div>
      </div>
    );
  };

  const renderRegularTable = (tableKey: string) => {
    const table = TABLES[tableKey as keyof typeof TABLES];
    if (table.type !== 'regular') return null;

    const seatsPerSide = 16;
    const isDragging = draggedTable === tableKey;

    return (
      <div
        key={tableKey}
        className={`table regular-table ${isDragging ? 'dragging' : ''}`}
        draggable
        onDragStart={() => handleTableDragStart(tableKey)}
        onDragOver={handleTableDragOver}
        onDrop={() => handleTableDrop(tableKey)}
      >
        <h4 className="table-header-draggable">
          <span className="drag-handle">⋮⋮</span>
          {table.name}
        </h4>
        <div className="table-layout vertical">
          <div className="table-end top">
            {renderSeat(tableKey, 0, 'end')}
          </div>
          <div className="table-sides">
            <div className="table-side left">
              {Array.from({ length: seatsPerSide }, (_, i) => renderSeat(tableKey, i + 1, 'left'))}
            </div>
            <div className="table-side right">
              {Array.from({ length: seatsPerSide }, (_, i) => renderSeat(tableKey, i + 1 + seatsPerSide, 'right'))}
            </div>
          </div>
          <div className="table-end bottom">
            {renderSeat(tableKey, seatsPerSide * 2 + 1, 'end')}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="seating-plan-loading">Chargement...</div>;
  }

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => window.print(), 100);
  };

  if (showPrintView) {
    return (
      <PrintView
        assignments={assignments}
        tableOrder={tableOrder}
        onBack={() => setShowPrintView(false)}
      />
    );
  }

  // Filter unassigned guests by search query
  const filteredUnassignedGuests = unassignedGuests.filter((guest) => {
    if (!searchQuery.trim()) return true;
    const fullName = `${guest.firstName} ${guest.lastName || ''}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase().trim());
  });

  return (
    <div className="seating-plan">
      <div className="seating-header">
        <h2>Plan de table</h2>
        <div className="seating-stats">
          <span>{unassignedGuests.length} invités non placés</span>
          <span>{assignments.size} places assignées</span>
          <button className="print-caterer-btn" onClick={handlePrint}>
            Imprimer pour le traiteur
          </button>
        </div>
      </div>

      <div className="rsvp-legend">
        <span className="legend-title">Légende RSVP :</span>
        <div className="legend-item">
          <span className="legend-color rsvp-confirmed"></span>
          <span>Confirmé</span>
        </div>
        <div className="legend-item">
          <span className="legend-color rsvp-declined"></span>
          <span>Décliné</span>
        </div>
        <div className="legend-item">
          <span className="legend-color rsvp-pending"></span>
          <span>En attente</span>
        </div>
      </div>

      <div className="seating-content">
        <div className="unassigned-guests">
          <h3>Invités non placés ({unassignedGuests.length})</h3>
          <input
            type="text"
            className="guest-search-input"
            placeholder="Rechercher un invité..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="guest-list">
            {filteredUnassignedGuests.map((guest) => {
              const rsvpStatus = getRsvpStatus(guest);
              return (
                <div
                  key={guest.id}
                  className={`guest-card unassigned rsvp-${rsvpStatus}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, guest)}
                >
                  {guest.firstName} {guest.lastName || ''}
                </div>
              );
            })}
            {filteredUnassignedGuests.length === 0 && searchQuery.trim() && (
              <div className="no-results">Aucun invité trouvé</div>
            )}
          </div>
        </div>

        <div className="tables-container">
          {renderHonorTable()}

          <div className="regular-tables">
            {tableOrder.map(key => renderRegularTable(key))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeatingPlan;
